/**
 * PropertySettings — per-property configuration page.
 *
 * All the per-property knobs that don't fit elsewhere:
 *
 *   - display_name             — human label
 *   - target_keywords          — comma-separated chip input
 *   - competitor_domains       — comma-separated chip input
 *   - brand_terms              — comma-separated chip input
 *   - audit_cron               — cron string for weekly audits
 *   - ahrefs_mode              — subdomains | domain | exact
 *   - ahrefs_target_override   — optional canonical target override
 *   - brand_radar_report_id    — Ahrefs Brand Radar Report ID
 *
 * The Crawl page already exposes crawl_schedule via the Scheduled
 * Crawls card; this page covers everything else.
 *
 * Save fires a PATCH with only the changed fields (model_dump
 * exclude_unset on the backend), so partial updates are cheap.
 */
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@tremor/react";
import {
  Settings as SettingsIcon, Save, Loader2, ArrowLeft, AlertTriangle,
  Check, Tag, Users, Megaphone, BarChart3, Sparkles, Globe, MapPin,
  Filter as FilterIcon,
} from "lucide-react";
import {
  useProperty, useUpdateProperty,
  type UpdatePropertyBody,
} from "@/lib/queries";
import { cn } from "@/lib/utils";


export default function PropertySettings() {
  const { propertyId: rawId } = useParams<{ propertyId: string }>();
  const propertyId = Number(rawId);
  const property = useProperty(propertyId);
  const update = useUpdateProperty();

  // Local form state — initialized from server, dirty-tracked
  const [displayName, setDisplayName] = useState("");
  const [targetKeywords, setTargetKeywords] = useState("");
  const [competitorDomains, setCompetitorDomains] = useState("");
  const [brandTerms, setBrandTerms] = useState("");
  const [auditCron, setAuditCron] = useState("");
  const [ahrefsMode, setAhrefsMode] = useState<"subdomains" | "domain" | "exact">("subdomains");
  const [ahrefsTargetOverride, setAhrefsTargetOverride] = useState("");
  const [brandRadarReportId, setBrandRadarReportId] = useState("");
  const [gbpListingName, setGbpListingName] = useState("");

  // Crawl configurator (Phase 1 — see migration 0022). Per-property
  // defaults that Frog will inherit on every crawl unless overridden
  // at start-time. The defaults that ship in the migration cover
  // typical WordPress/WooCommerce filter-explosion patterns.
  const [excludeUrlPatterns, setExcludeUrlPatterns] = useState("");
  const [includeUrlPatterns, setIncludeUrlPatterns] = useState("");
  const [stripQueryParams, setStripQueryParams] = useState("");
  const [maxCrawlDepth, setMaxCrawlDepth] = useState<number>(0);

  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize from server once
  useEffect(() => {
    if (!property.data) return;
    const p = property.data;
    setDisplayName(p.display_name);
    setTargetKeywords((p.target_keywords ?? []).join(", "));
    setCompetitorDomains((p.competitor_domains ?? []).join(", "));
    setBrandTerms((p.brand_terms ?? []).join(", "));
    setAuditCron(p.audit_cron);
    setAhrefsMode((p.ahrefs_mode as "subdomains" | "domain" | "exact" | null) ?? "subdomains");
    setAhrefsTargetOverride(p.ahrefs_target_override ?? "");
    setBrandRadarReportId(p.brand_radar_report_id ?? "");
    setGbpListingName(p.gbp_listing_name ?? "");
    setExcludeUrlPatterns((p.exclude_url_patterns ?? []).join("\n"));
    setIncludeUrlPatterns((p.include_url_patterns ?? []).join("\n"));
    setStripQueryParams((p.strip_query_params ?? []).join(", "));
    setMaxCrawlDepth(p.max_crawl_depth ?? 0);
  }, [property.data]);

  const onSave = async () => {
    setError(null);
    setSaved(false);
    const body: UpdatePropertyBody = {
      display_name: displayName,
      target_keywords: targetKeywords.split(",").map((s) => s.trim()).filter(Boolean),
      competitor_domains: competitorDomains.split(",").map((s) => s.trim()).filter(Boolean),
      brand_terms: brandTerms.split(",").map((s) => s.trim()).filter(Boolean),
      audit_cron: auditCron,
      ahrefs_mode: ahrefsMode,
      ahrefs_target_override: ahrefsTargetOverride || "",  // "" clears
      brand_radar_report_id: brandRadarReportId || "",     // "" clears
      gbp_listing_name: gbpListingName || "",              // "" clears
      exclude_url_patterns: excludeUrlPatterns
        .split("\n").map((s) => s.trim()).filter(Boolean),
      include_url_patterns: includeUrlPatterns
        .split("\n").map((s) => s.trim()).filter(Boolean),
      strip_query_params: stripQueryParams
        .split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
      max_crawl_depth: Math.max(0, Math.min(20, Number.isFinite(maxCrawlDepth) ? maxCrawlDepth : 0)),
    };
    try {
      await update.mutateAsync({ id: propertyId, body });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to save");
    }
  };

  if (property.isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!property.data) return <div className="text-sm text-red-600">Property not found.</div>;
  const p = property.data;

  return (
    <div className="space-y-5 max-w-3xl">
      <div>
        <Link to={`/p/${propertyId}/overview`}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3 w-3" /> Property overview
        </Link>
        <div className="mt-1 flex items-center gap-2">
          <SettingsIcon className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">Property settings</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          <span className="font-mono">{p.domain}</span> · {p.display_name}
        </p>
      </div>

      {/* Identity */}
      <Section title="Identity" icon={<Globe className="h-4 w-4 text-primary" />}>
        <Field label="Domain" hint="Immutable. Domain change = new property.">
          <input type="text" value={p.domain} disabled
            className="w-full rounded-md border border-input bg-muted px-3 py-1.5 font-mono text-sm text-muted-foreground" />
        </Field>
        <Field label="Display name" hint="Human-friendly label shown in lists + headers.">
          <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm" />
        </Field>
      </Section>

      {/* SEO targeting */}
      <Section title="SEO targeting" icon={<Tag className="h-4 w-4 text-primary" />}>
        <Field label="Target keywords" hint="Comma-separated. Tracked for ranking + used as seed for DFS labs / SERP pulls.">
          <textarea value={targetKeywords} onChange={(e) => setTargetKeywords(e.target.value)}
            rows={3}
            placeholder="airsoft, magazin airsoft, replici airsoft"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono" />
        </Field>
        <Field label="Competitor domains" hint="Comma-separated. Used in cross-property SOV + backlink-intersection.">
          <textarea value={competitorDomains} onChange={(e) => setCompetitorDomains(e.target.value)}
            rows={2}
            placeholder="airsoft6.ro, swat.ro"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono" />
        </Field>
        <Field label="Brand terms" hint="Comma-separated. Used in Brand Radar + content-mention monitoring.">
          <textarea value={brandTerms} onChange={(e) => setBrandTerms(e.target.value)}
            rows={2}
            placeholder="nrg airsoft, nrg.ro"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono" />
        </Field>
      </Section>

      {/* Ahrefs */}
      <Section title="Ahrefs Site Explorer" icon={<BarChart3 className="h-4 w-4 text-primary" />}>
        <Field label="Mode" hint={
          <>Controls which subdomains Ahrefs includes:
            <strong> subdomains</strong> = all (incl. cloud., shop., etc — best for greenfield).
            <strong> domain</strong> = apex + www (excludes sibling subs).
            <strong> exact</strong> = match target literally (best with override).
          </>
        }>
          <select value={ahrefsMode} onChange={(e) => setAhrefsMode(e.target.value as "subdomains" | "domain" | "exact")}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm">
            <option value="subdomains">subdomains (default — includes all sub-domains)</option>
            <option value="domain">domain (apex + www, excludes sibling subs)</option>
            <option value="exact">exact (only the literal target string)</option>
          </select>
        </Field>
        <Field label="Target override" hint={
          <>Optional. When the canonical lives at www.&lt;domain&gt; and you use <strong>mode=exact</strong> or <strong>domain</strong>,
          set this to <span className="font-mono">www.{p.domain}</span>. Empty = use bare domain.</>
        }>
          <input type="text" value={ahrefsTargetOverride} onChange={(e) => setAhrefsTargetOverride(e.target.value)}
            placeholder={`www.${p.domain}`}
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono" />
        </Field>
      </Section>

      {/* Google Business Profile */}
      <Section title="Google Business Profile" icon={<MapPin className="h-4 w-4 text-primary" />}>
        <Field label="GBP listing name" hint={
          <>
            The exact name of this property's <strong>Google Business
            Profile listing</strong> (e.g. <span className="font-mono">nrg
            showroom Constanța</span>). This may differ from both the
            domain and the display name. When set, the DataForSEO bulk
            pull fires Google reviews + my-business-info using this
            keyword. Leave empty if the property has no GBP presence —
            both endpoints stay skipped. <Link to={`/p/${propertyId}/reviews`} className="text-primary hover:underline">→ Reviews tab</Link>
          </>
        }>
          <input type="text" value={gbpListingName} onChange={(e) => setGbpListingName(e.target.value)}
            placeholder="(empty = no GBP listing)"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm" />
        </Field>
      </Section>

      {/* Brand Radar */}
      <Section title="AI search visibility (Brand Radar)" icon={<Sparkles className="h-4 w-4 text-primary" />}>
        <Field label="Brand Radar Report ID" hint={
          <>From the Ahrefs dashboard → Brand Radar → your Report. Empty = AI visibility tab disabled. <Link to={`/p/${propertyId}/ahrefs`} className="text-primary hover:underline">→ AI visibility tab</Link></>
        }>
          <input type="text" value={brandRadarReportId} onChange={(e) => setBrandRadarReportId(e.target.value)}
            placeholder="(paste Report ID, e.g. report_abc123)"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono" />
        </Field>
      </Section>

      {/* Crawl scope (Phase 1 of the gregorco fix — see migration 0022) */}
      <Section title="Crawl scope" icon={<FilterIcon className="h-4 w-4 text-primary" />}>
        <Field label="Exclude URL patterns" hint={
          <>
            One regex pattern per line. URLs matching any pattern are
            skipped during crawl. Defaults cover WordPress + WooCommerce
            filter combinatorics (<span className="font-mono">?filter_, ?orderby=, ?min_price=</span>, etc.)
            and admin areas (<span className="font-mono">/wp-admin/, /cart/, /checkout/</span>). Edit only if
            the property has unusual URL patterns or you want to crawl
            something normally excluded.
          </>
        }>
          <textarea value={excludeUrlPatterns}
            onChange={(e) => setExcludeUrlPatterns(e.target.value)}
            rows={8}
            placeholder={`\\?filter_\n\\?orderby=\n/wp-admin/\n…one per line`}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono" />
        </Field>

        <Field label="Include URL patterns (whitelist)" hint={
          <>
            Optional regex whitelist — when <strong>non-empty</strong>,
            ONLY URLs matching at least one pattern are crawled (in
            addition to passing the excludes above). Empty = crawl
            everything (after excludes). Useful for "audit just /shop/*"
            type scopes.
          </>
        }>
          <textarea value={includeUrlPatterns}
            onChange={(e) => setIncludeUrlPatterns(e.target.value)}
            rows={4}
            placeholder="(leave empty unless you want a strict whitelist)"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono" />
        </Field>

        <Field label="Strip query parameters (dedup)" hint={
          <>
            Comma or whitespace separated. These params are stripped
            from URLs before Frog decides "have I seen this URL?", so
            <span className="font-mono">?utm_source=A</span> and
            <span className="font-mono">?utm_source=B</span> collapse to
            one entry. Defaults cover UTM + common tracking params.
          </>
        }>
          <input type="text" value={stripQueryParams}
            onChange={(e) => setStripQueryParams(e.target.value)}
            placeholder="utm_source, utm_medium, gclid, fbclid…"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs font-mono" />
        </Field>

        <Field label="Max crawl depth" hint={
          <>
            Clicks from the start URL. <strong>0 = unlimited</strong>.
            WordPress/WooCommerce sites typically need ~5 to avoid the
            pagination + filter-combo explosion that ate the gregorco
            crawl. 3 is tight (good for first-pass audits); 5 is the
            sweet spot for full audits; 10 means "go deep, accept the cost".
          </>
        }>
          <input type="number" min={0} max={20}
            value={maxCrawlDepth}
            onChange={(e) => setMaxCrawlDepth(Number(e.target.value) || 0)}
            className="w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm" />
        </Field>
      </Section>

      {/* Cadence */}
      <Section title="Audit cadence" icon={<Megaphone className="h-4 w-4 text-primary" />}>
        <Field label="Audit cron" hint={<>5-field cron string. When this fires (system time), the weekly audit pipeline runs. Crawl scheduling is set on the <Link to={`/p/${propertyId}/crawl`} className="text-primary hover:underline">Crawl page</Link>.</>}>
          <input type="text" value={auditCron} onChange={(e) => setAuditCron(e.target.value)}
            placeholder="0 8 * * 1"
            className="w-full rounded-md border border-input bg-background px-3 py-1.5 font-mono text-sm" />
        </Field>
      </Section>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2">
        <button type="button" onClick={onSave}
          disabled={update.isPending}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save changes
        </button>
        {saved && (
          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-1 text-xs text-emerald-700 dark:text-emerald-400">
            <Check className="h-3 w-3" /> Saved
          </span>
        )}
        {error && (
          <span className="inline-flex items-center gap-1 rounded-md bg-red-500/10 px-2 py-1 text-xs text-red-700 dark:text-red-400">
            <AlertTriangle className="h-3 w-3" /> {error}
          </span>
        )}
      </div>

      {/* Audience-info quick-card */}
      <Card>
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-medium">Tenant info</h3>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-muted-foreground">Tenant:</span> <span className="font-mono">{p.tenant_id}</span></div>
          <div><span className="text-muted-foreground">Property ID:</span> <span className="font-mono">{p.id}</span></div>
          <div><span className="text-muted-foreground">Created:</span> <span className="font-mono">{p.created_at.slice(0, 10)}</span></div>
          <div><span className="text-muted-foreground">Updated:</span> <span className="font-mono">{p.updated_at.slice(0, 10)}</span></div>
        </div>
      </Card>
    </div>
  );
}


function Section({ title, icon, children }: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <div className="flex items-center gap-2">
        {icon}
        <h2 className="text-sm font-semibold">{title}</h2>
      </div>
      <div className="mt-3 space-y-3">
        {children}
      </div>
    </Card>
  );
}


function Field({ label, hint, children }: {
  label: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium">{label}</label>
      {children}
      {hint && (
        <p className={cn("text-[11px] leading-snug text-muted-foreground")}>{hint}</p>
      )}
    </div>
  );
}
